import { mapKeys, mapValues } from 'es-toolkit';
import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';

import { ReadFile, useHiddenFileSelect } from '@paragon/client-tools';
import { designService } from '@paragon/data-connections';
import { enqueueError, InlineNotification } from '@paragon/notification-tools';
import { notNullish } from '@paragon/utilities';

import { getComponentNames } from '../../store/selectors/layoutModels';
import { useAppDispatch, useAppSelector } from '../hooks';

interface Props {
  readonly quantities: { [guid: string]: number };
  readonly onQuantityChange: (
    quantities: { [guid: string]: number },
    sourceFile: ReadFile,
    fileContainsOtherComponents: boolean,
  ) => void;
  readonly acceptCntOnly?: boolean;
}

export function CountFileUploader(props: Props) {
  const { quantities, onQuantityChange, acceptCntOnly } = props;
  const dispatch = useAppDispatch();

  const [isUploading, setIsUploading] = useState(false);
  const [fileContainsOtherComponents, setFileContainsOtherComponents] = useState<string | null>(null);

  const componentNames = useAppSelector(getComponentNames);
  const [renderHiddenSelect, selectFile] = useHiddenFileSelect(acceptCntOnly ? '.cnt' : '.cnt,.seq', false);

  async function onSelectFileClick() {
    const chosenFile = await selectFile();

    setIsUploading(true);

    try {
      const serverResponse = await designService.countUtilities.postParseQuantitiesFromCountFile(chosenFile);
      const parsedQuantities = mapKeys(serverResponse, (_, name) => name.toLocaleLowerCase());

      const selectedComponentGuids = Object.keys(quantities);
      const selectedComponentNames = new Set(
        selectedComponentGuids
          .map((guid) => componentNames[guid])
          .filter(notNullish)
          .map((name) => name.toLocaleLowerCase()),
      );

      const parsedComponentNames = Object.keys(parsedQuantities);
      const parsedComponentNamesNotSelected = parsedComponentNames
        .filter((name) => !selectedComponentNames.has(name))
        .join(', ');

      const updatedFileContainsOtherComponents =
        parsedComponentNamesNotSelected.length > 0
          ? `Other components were found in the uploaded quantity file(s): ${parsedComponentNamesNotSelected}`
          : null;
      setFileContainsOtherComponents(updatedFileContainsOtherComponents);

      const updatedQuantities = mapValues(
        quantities,
        (_, componentDesignGuid) => parsedQuantities[componentNames[componentDesignGuid].toLocaleLowerCase()] ?? 0,
      );

      onQuantityChange(updatedQuantities, chosenFile, !!updatedFileContainsOtherComponents);
    } catch (err) {
      console.error(err);
      dispatch(enqueueError('Error parsing count file.  Please contact Paragon Customer Support.'));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div>
      {renderHiddenSelect()}

      <Button fullWidth variant="outlined" disabled={isUploading} onClick={onSelectFileClick}>
        Upload {acceptCntOnly ? 'CNT' : 'Quantity'} File
      </Button>

      {isUploading && <CircularProgress />}
      {!isUploading && fileContainsOtherComponents && (
        <InlineNotification fullWidthWrap type="error">
          {fileContainsOtherComponents}
        </InlineNotification>
      )}
    </div>
  );
}
